// Centralised date/time formatting for the whole web app.
//
// The backend returns timestamps as ISO 8601 UTC strings ("2026-10-02T09:35:00.000Z";
// backend db.js reads zone-less `timestamp` columns as UTC too). Everything is rendered
// in Asia/Tashkent (UTC+5, no DST) via Intl.DateTimeFormat, so the output never depends
// on the browser's timezone: 09:35Z → 14:35. Seconds are never displayed.
//
// Inputs: ISO string (a string with no zone designator is treated as UTC), epoch
// milliseconds (number or digit string, e.g. Payme times) or a Date. A bare
// "YYYY-MM-DD" is a calendar date, not an instant — it is shown as-is, unshifted.

export const APP_TIME_ZONE = 'Asia/Tashkent';

const fmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: APP_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_ZONE = /(Z|[+-]\d{2}(:?\d{2})?)$/i;

function parts(value) {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) {
    const [y, mo, d] = value.trim().split('-');
    return { y, mo, d, hh: null, mm: null };
  }

  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number' || /^\d+$/.test(String(value).trim())) {
    date = new Date(Number(value));
  } else {
    let s = String(value).trim().replace(' ', 'T');
    if (!HAS_ZONE.test(s.slice(10))) s += 'Z';
    date = new Date(s);
  }
  if (Number.isNaN(date.getTime())) return null;

  const p = Object.fromEntries(fmt.formatToParts(date).map((x) => [x.type, x.value]));
  return { y: p.year, mo: p.month, d: p.day, hh: p.hour, mm: p.minute };
}

// "DD.MM.YYYY" — empty string if unparseable.
export function formatDate(value) {
  const p = parts(value);
  return p ? `${p.d}.${p.mo}.${p.y}` : '';
}

// "HH:mm" — empty string if the value carried no time component.
export function formatTime(value) {
  const p = parts(value);
  return p && p.hh ? `${p.hh}:${p.mm}` : '';
}

// "DD.MM.YYYY HH:mm" (falls back to just the date for date-only values).
export function formatDateTime(value) {
  const p = parts(value);
  if (!p) return '';
  return p.hh ? `${p.d}.${p.mo}.${p.y} ${p.hh}:${p.mm}` : `${p.d}.${p.mo}.${p.y}`;
}
